import { useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { Dropdown, MenuProps } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as FileSaver from 'file-saver';
import { handleMenuClick, generateMenuItems } from 'utils/excelUtils';

import { formatCurrency, getEarliestValidDate, toLocalTime } from 'utils';
import useCommonStore from 'stores/useCommon';
import { useGetTransactionsPaymentAcc } from 'hooks/useGetPayments';
import { PAYMENT_STATUS_ACC_NAMES } from 'constants/form';

import { PermissionActionTypes } from 'helper/type/action.type';
import IBButton from 'helper/permission.action';

interface Props {
  fileName: string;
  selectedPaymentState?: string;
  selectedPaymentMethod?: string;
  filterOptions: any;
  t: (key: string) => string;
}

const ExportTransactionsAcc = ({
  fileName,
  selectedPaymentState,
  selectedPaymentMethod,
  filterOptions,
  t
}: Props) => {
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [enable, setEnable] = useState<boolean>(false);

  const {
    data: dataToExport,
    total_amount,
    total_count
  } = useGetTransactionsPaymentAcc(filterOptions, 0, 0, enable);

  const { currentBranch } = useCommonStore();

  useEffect(() => {
    if (isLoadingExport) {
      setEnable(false);
    } else {
      setEnable(true);
    }
  }, [isLoadingExport]);

  const filteredData = useMemo(() => {
    return (dataToExport || []).filter(item => item.state !== 'cancelled');
  }, [dataToExport]);

  const fromDate = getEarliestValidDate(filterOptions.start_date_acc);
  const toDate = getEarliestValidDate(filterOptions.end_date_acc);
  // Payment status data from API
  const paymentStatusCounts = {
    stayed: total_count?.total_count_stayed || 0,
    inHouse: total_count?.total_count_in_house || 0,
    deposit: total_count?.total_count_deposit || 0,
    invoiced: total_count?.total_count_invoiced || 0,
    total_count: total_count?.total_count_total || 0
  };

  const paymentStatusAmounts = {
    stayed: total_amount?.total_amount_stayed || 0,
    inHouse: total_amount?.total_amount_in_house || 0,
    deposit: total_amount?.total_amount_deposit || 0,
    invoiced: total_amount?.total_amount_invoiced || 0,
    total_amount: total_amount?.total_amount_total || 0
  };

  const exportToCSV = async () => {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    try {
      setIsLoadingExport(true);

      const workbook = new ExcelJS.Workbook();
      const sheetName = t('managePage.transactionPage.export.sheetName');
      const sheet = workbook.addWorksheet(sheetName, {
        views: [
          {
            showGridLines: false
          }
        ]
      });

      // Title
      sheet.mergeCells('A1:Q1');
      sheet.getCell('A1').value = currentBranch?.name?.toUpperCase() || '';
      sheet.getCell('A1').font = { bold: true, size: 14 };
      sheet.getCell('A1').alignment = { horizontal: 'center' };

      sheet.mergeCells('A2:Q2');
      sheet.getCell('A2').value = t('managePage.transactionPage.export.reportTitle');
      sheet.getCell('A2').font = { size: 11, bold: true };
      sheet.getCell('A2').alignment = { horizontal: 'center' };

      sheet.getCell('A4').value = t('common.time.fromDate');
      sheet.getCell('A4').font = { bold: true };
      sheet.getCell('B4').value = fromDate;
      sheet.getCell('C4').value = t('common.time.toDate');
      sheet.getCell('C4').font = { bold: true };
      sheet.getCell('D4').value = toDate;
      sheet.getCell('E4').value = t('common.bookingData.status');
      sheet.getCell('E4').font = { bold: true };
      sheet.getCell('F4').value = selectedPaymentState || t('common.statuses.all');
      sheet.getCell('G4').value = t('managePage.transactionPage.common.paymentMethod');
      sheet.getCell('G4').font = { bold: true };
      sheet.getCell('H4').value = selectedPaymentMethod || t('common.statuses.all');
      sheet.getCell('I4').value = t('managePage.transactionPage.acc.paymentStatus');
      sheet.getCell('I4').font = { bold: true };
      sheet.getCell('J4').value = filterOptions.payment_status
        ? PAYMENT_STATUS_ACC_NAMES[
            filterOptions.payment_status as keyof typeof PAYMENT_STATUS_ACC_NAMES
          ] || filterOptions.payment_status
        : t('common.statuses.all');

      // Header row
      sheet.getRow(5).values = [
        t('managePage.transactionPage.export.id'),
        t('managePage.transactionPage.export.bookingCode'),
        t('common.bookingData.originalBookingId'),
        t('common.bookingData.checkInDate'),
        t('common.bookingData.checkOutDate'),
        t('managePage.transactionPage.export.travelAgencyCompany'),
        t('managePage.transactionPage.common.paymentDate'),
        t('common.note'),
        t('managePage.transactionPage.common.amount'),
        t('managePage.transactionPage.export.paymentTypeColumn'),
        t('managePage.transactionPage.common.paymentMethod'),
        t('managePage.transactionPage.acc.paymentStatus'),
        t('managePage.transactionPage.common.userName'),
        t('common.customer.name'),
        t('common.bookingData.debitPartner'),
        t('managePage.transactionPage.common.desc'),
        t('managePage.transactionPage.v2.createdDate')
      ];
      sheet.getRow(5).eachCell(cell => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'left' };
      });

      sheet.columns = [
        { key: 'id', width: 10 },
        { key: 'booking_id', width: 12 },
        { key: 'original_booking_id', width: 12 },
        { key: 'checkin_date', width: 18 },
        { key: 'checkout_date', width: 18 },
        { key: 'travel_agency_name', width: 20 },
        { key: 'payment_date', width: 20 },
        { key: 'note', width: 25 },
        { key: 'amount', width: 18 },
        { key: 'payment_type', width: 15 },
        { key: 'payment_method', width: 25 },
        { key: 'payment_status', width: 18 },
        { key: 'user_name', width: 20 },
        { key: 'customer_name', width: 20 },
        { key: 'debit_partner_name', width: 22 },
        { key: 'description', width: 25 },
        { key: 'created_date', width: 22 }
      ];

      filteredData.forEach(item => {
        const row = sheet.addRow({
          id: item.id,
          booking_id: item.booking_id ? item.booking_id : '',
          original_booking_id: item.original_booking_id ? item.original_booking_id : '',
          checkin_date: item.checkin_date ? toLocalTime(item.checkin_date, 'DD/MM/YYYY') : '',
          checkout_date: item.checkout_date ? toLocalTime(item.checkout_date, 'DD/MM/YYYY') : '',
          travel_agency_name: item.travel_agency_name || '',
          payment_date: toLocalTime(item.payment_date, 'DD/MM/YYYY HH:mm'),
          note: item.note ? item.note : '',
          amount: formatCurrency(item.amount),
          payment_type: item.payment_type,
          payment_method: item.payment_method,
          payment_status:
            PAYMENT_STATUS_ACC_NAMES[
              item.payment_status as keyof typeof PAYMENT_STATUS_ACC_NAMES
            ] ||
            item.payment_status ||
            '',
          user_name: item.user_name,
          customer_name: item.customer_name || '',
          debit_partner_name: item.debit_partner_name || '',
          description: item.description || '',
          created_date: item.created_date
            ? toLocalTime(item.created_date, 'DD/MM/YYYY HH:mm:ss')
            : ''
        });

        const getPaymentStatusColor = (status: string) => {
          switch (status?.toLowerCase()) {
            case 'stayed':
              return 'FF52C41A'; // green
            case 'in_house':
              return 'FFD4AF37'; // brownish yellow
            case 'deposit':
              return 'FF1890FF'; // blue
            case 'invoiced':
              return 'FF4D4F'; // red
            default:
              return 'FF000000'; // black (default)
          }
        };

        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          // Amount column (column 9) should be right-aligned
          if (colNumber === 9) {
            cell.alignment = { horizontal: 'right' };
          } else {
            cell.alignment = { horizontal: 'left' };
          }

          // Payment status column (column 12) should have colored text
          if (colNumber === 12) {
            cell.font = {
              color: { argb: getPaymentStatusColor(item.payment_status) },
              bold: true
            };
          }
        });
      });

      const totalRowIndex = filteredData.length + 6;

      sheet.mergeCells(`A${totalRowIndex}:H${totalRowIndex}`);
      sheet.getCell(`A${totalRowIndex}`).value = t('common.total');
      sheet.getCell(`A${totalRowIndex}`).font = { bold: true };
      sheet.getCell(`A${totalRowIndex}`).alignment = { horizontal: 'center' };

      sheet.getCell(`I${totalRowIndex}`).value = formatCurrency(
        total_amount?.total_amount_total || 0
      );
      sheet.getCell(`I${totalRowIndex}`).font = { bold: true };
      sheet.getCell(`I${totalRowIndex}`).alignment = { horizontal: 'right' };

      sheet.getCell(`L${totalRowIndex}`).value = total_count?.total_count_total || 0;
      sheet.getCell(`L${totalRowIndex}`).font = { bold: true };
      sheet.getCell(`L${totalRowIndex}`).alignment = { horizontal: 'center' };

      ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q'].forEach(
        col => {
          sheet.getCell(`${col}${totalRowIndex}`).border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          };
        }
      );

      const paymentStatusStartRow = totalRowIndex + 2;

      sheet.mergeCells(`A${paymentStatusStartRow}:C${paymentStatusStartRow}`);
      sheet.getCell(`A${paymentStatusStartRow}`).value = t(
        'managePage.transactionPage.export.totalPaymentStatus'
      );
      sheet.getCell(`A${paymentStatusStartRow}`).font = { bold: true, size: 12 };
      sheet.getCell(`A${paymentStatusStartRow}`).alignment = { horizontal: 'center' };
      ['A', 'B', 'C'].forEach(col => {
        sheet.getCell(`${col}${paymentStatusStartRow}`).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE6F7FF' }
        };
      });

      // Add header row for payment status table
      const headerRowIndex = paymentStatusStartRow + 1;
      sheet.getCell(`A${headerRowIndex}`).value = t('managePage.transactionPage.acc.paymentStatus');
      sheet.getCell(`A${headerRowIndex}`).font = { bold: true };
      sheet.getCell(`B${headerRowIndex}`).value = t('managePage.transactionPage.export.count');
      sheet.getCell(`B${headerRowIndex}`).font = { bold: true };
      sheet.getCell(`C${headerRowIndex}`).value = t('managePage.transactionPage.common.amount');
      sheet.getCell(`C${headerRowIndex}`).font = { bold: true };
      sheet.getCell(`C${headerRowIndex}`).alignment = { horizontal: 'right' };

      // Stayed row
      const stayedRowIndex = paymentStatusStartRow + 2;
      sheet.getCell(`A${stayedRowIndex}`).value = `${PAYMENT_STATUS_ACC_NAMES.stayed}`;
      sheet.getCell(`A${stayedRowIndex}`).font = { bold: true, color: { argb: 'FF52C41A' } };
      sheet.getCell(`B${stayedRowIndex}`).value = paymentStatusCounts.stayed;
      sheet.getCell(`B${stayedRowIndex}`).font = { bold: true, color: { argb: 'FF52C41A' } };
      sheet.getCell(`C${stayedRowIndex}`).value = formatCurrency(paymentStatusAmounts.stayed);
      sheet.getCell(`C${stayedRowIndex}`).font = { bold: true, color: { argb: 'FF52C41A' } };
      sheet.getCell(`C${stayedRowIndex}`).alignment = { horizontal: 'right' };

      // In House row
      const inHouseRowIndex = paymentStatusStartRow + 3;
      sheet.getCell(`A${inHouseRowIndex}`).value = `${PAYMENT_STATUS_ACC_NAMES.in_house}`;
      sheet.getCell(`A${inHouseRowIndex}`).font = { bold: true, color: { argb: 'FFD4AF37' } };
      sheet.getCell(`B${inHouseRowIndex}`).value = paymentStatusCounts.inHouse;
      sheet.getCell(`B${inHouseRowIndex}`).font = { bold: true, color: { argb: 'FFD4AF37' } };
      sheet.getCell(`C${inHouseRowIndex}`).value = formatCurrency(paymentStatusAmounts.inHouse);
      sheet.getCell(`C${inHouseRowIndex}`).font = { bold: true, color: { argb: 'FFD4AF37' } };
      sheet.getCell(`C${inHouseRowIndex}`).alignment = { horizontal: 'right' };

      // Deposit row
      const depositRowIndex = paymentStatusStartRow + 4;
      sheet.getCell(`A${depositRowIndex}`).value = `${PAYMENT_STATUS_ACC_NAMES.deposit}`;
      sheet.getCell(`A${depositRowIndex}`).font = { bold: true, color: { argb: 'FF1890FF' } };
      sheet.getCell(`B${depositRowIndex}`).value = paymentStatusCounts.deposit;
      sheet.getCell(`B${depositRowIndex}`).font = { bold: true, color: { argb: 'FF1890FF' } };
      sheet.getCell(`C${depositRowIndex}`).value = formatCurrency(paymentStatusAmounts.deposit);
      sheet.getCell(`C${depositRowIndex}`).font = { bold: true, color: { argb: 'FF1890FF' } };
      sheet.getCell(`C${depositRowIndex}`).alignment = { horizontal: 'right' };

      // Invoiced row
      const invoicedRowIndex = paymentStatusStartRow + 5;
      sheet.getCell(`A${invoicedRowIndex}`).value = `${PAYMENT_STATUS_ACC_NAMES.invoiced}`;
      sheet.getCell(`A${invoicedRowIndex}`).font = { bold: true, color: { argb: 'FFFF4D4F' } };
      sheet.getCell(`B${invoicedRowIndex}`).value = paymentStatusCounts.invoiced;
      sheet.getCell(`B${invoicedRowIndex}`).font = { bold: true, color: { argb: 'FFFF4D4F' } };
      sheet.getCell(`C${invoicedRowIndex}`).value = formatCurrency(paymentStatusAmounts.invoiced);
      sheet.getCell(`C${invoicedRowIndex}`).font = { bold: true, color: { argb: 'FFFF4D4F' } };
      sheet.getCell(`C${invoicedRowIndex}`).alignment = { horizontal: 'right' };

      // Total row for payment status
      const paymentStatusTotalRowIndex = paymentStatusStartRow + 6;
      sheet.getCell(`A${paymentStatusTotalRowIndex}`).value = t('common.total');
      sheet.getCell(`A${paymentStatusTotalRowIndex}`).font = {
        bold: true,
        color: { argb: 'FF000000' }
      };
      sheet.getCell(`B${paymentStatusTotalRowIndex}`).value = paymentStatusCounts.total_count;
      sheet.getCell(`B${paymentStatusTotalRowIndex}`).font = {
        bold: true,
        color: { argb: 'FF000000' }
      };
      sheet.getCell(`C${paymentStatusTotalRowIndex}`).value = formatCurrency(
        paymentStatusAmounts.total_amount
      );
      sheet.getCell(`C${paymentStatusTotalRowIndex}`).font = {
        bold: true,
        color: { argb: 'FF000000' }
      };
      sheet.getCell(`C${paymentStatusTotalRowIndex}`).alignment = { horizontal: 'right' };

      // Add borders to payment status section (columns A, B, and C)
      const paymentHeaderRowIndex = paymentStatusStartRow + 1;
      const paymentInvoicedRowIndex = paymentStatusStartRow + 5;
      const paymentTotalRowIndex = paymentStatusStartRow + 6;
      [
        paymentStatusStartRow,
        paymentHeaderRowIndex,
        stayedRowIndex,
        inHouseRowIndex,
        depositRowIndex,
        paymentInvoicedRowIndex,
        paymentTotalRowIndex
      ].forEach(rowIndex => {
        ['A', 'B', 'C'].forEach(col => {
          sheet.getCell(`${col}${rowIndex}`).border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const data = new Blob([buffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (e) {
      return { status: 'ERROR', error: e };
    } finally {
      setIsLoadingExport(false);
    }
  };

  const handleClickMenu = (menu: any) => {
    handleMenuClick(menu.key, exportToCSV);
  };
  const items: MenuProps['items'] = useMemo(() => generateMenuItems(), []);

  return (
    <Dropdown
      menu={{ items, onClick: handleClickMenu }}
      trigger={['click']}
      overlayClassName="menu-user"
    >
      <IBButton
        permission={PermissionActionTypes.TRANSACTION_LIST_EXPORT}
        icon={<DownloadOutlined />}
        loading={isLoadingExport}
      >
        {t('common.actions.exportData')}
      </IBButton>
    </Dropdown>
  );
};

export default ExportTransactionsAcc;
